import React, { useState, Fragment } from 'react'
import { Image, isMultipleSelect, isMultipleSelectAnswer } from '../../utils'
import { QuestionChoice, variantTypes } from '@mission.io/question-toolkit'
import { QuestionVariant } from '../../model/general'
import { MultipleSelectProps } from './MultipleSelect'
import Footer from '../../components/studentPromptFooter'
import ChoiceBox from '../../components/choice-box'
import {
	LabelInformation,
	ChoiceWrapper,
	GeneralScoreTitle,
} from '../../assets/styles'
import { ReviewMultipleChoice as ReviewMultipleSelect } from '../multiple-choice/Student'
import { FaCheck, FaTimes } from 'react-icons/fa'
import FooterStickyContainer from '../../components/footer-sticky-container/FooterStickyContainer'
import OptionalMarkdown from '../../components/Markdown/OptionalMarkdown'

/**
 * A component for a student view where the student can answer the multiple select question. It also
 * has a review view where the student can see the correct answer of the question
 * @returns {JSX.Element}
 */
export const StudentMultipleChoice = (
	props: MultipleSelectProps
): JSX.Element => {
	const {
		question,
		textToSpeech: TextToSpeech,
		requestId,
		review,
		studentResponse,
		parentRef,
		sendMessage,
		classResponse,
	} = props
	if (!question || !isMultipleSelect(question)) return <Fragment />

	const [selectedChoices, setSelectedChoices] = useState<string[]>([])
	const [error, setError] = useState<string>('')

	const choicesHaveImage = Boolean(
		question?.choices.some((choice: QuestionChoice) => !!choice.media?.[0])
	)

	const handleSubmit = () => {
		if (selectedChoices.length === 0) {
			setError('Please Make a Choice')
			return
		}
		setError('')
		sendMessage &&
			sendMessage({
				type: QuestionVariant.MULTIPLE_SELECT,
				value: selectedChoices,
			})
	}

	const handleSelected = (choiceId: string) => {
		setError('')
		setSelectedChoices(state => {
			const index = state.indexOf(choiceId)
			const newState = [...state]
			newState.splice(index, 1)
			if (index > -1) {
				return newState
			} else {
				return [choiceId, ...state]
			}
		})
	}

	const instantReview = review === 'INSTANT'
	let studentAnswer: string[] = selectedChoices
	if (instantReview) {
		if (!studentResponse || !isMultipleSelectAnswer(studentResponse.answer)) {
			studentAnswer = []
		} else {
			studentAnswer = studentResponse?.answer.value
		}
	}

	const isSelected = (choiceId: string) => {
		return Boolean(studentAnswer.find(i => i === choiceId))
	}
	if (question)
		return instantReview || (!review && requestId) ? (
			<FooterStickyContainer
				instantReview={instantReview}
				footerContent={
					<Footer
						question={question}
						error={error}
						onClickSubmit={handleSubmit}
						isReview={instantReview}
					/>
				}
				topContent={
					<>
						{classResponse && (
							<GeneralScoreTitle>
								<div className="question-index">{`${props.questionIndex}.`}</div>
								{`${(
									classResponse.generalScore * 100
								).toFixed()}% Answered Correctly`}
							</GeneralScoreTitle>
						)}
						<LabelInformation>
							<OptionalMarkdown
								className="prose-on-game-light-purple"
								textToSpeech={TextToSpeech}>
								{question.phrase}
							</OptionalMarkdown>
						</LabelInformation>
						{!instantReview && (
							<div className="subtitle">Select all that apply</div>
						)}
						<Image media={question.media} expandable parentRef={parentRef} />
						<ChoiceWrapper $withImage={choicesHaveImage}>
							{question.choices.map((choice: QuestionChoice, i) => {
								const selected = isSelected(String(choice._id))
								const correct = choice.answer
								const wrong = selected && instantReview && !correct
								const optionScore =
									classResponse &&
									classResponse.scores.type === variantTypes.MULTIPLE_SELECT &&
									classResponse.scores.optionScores.find(
										i => i._id === String(choice._id)
									)?.score
								return (
									<ChoiceBox
										key={String(choice._id)}
										isReview={instantReview}
										selected={selected}
										onClick={() => handleSelected(String(choice._id))}
										choice={choice}
										textToSpeech={TextToSpeech}
										classResponse={classResponse}
										parentRef={parentRef}
										type="MULTIPLE_SELECT"
										optionScore={optionScore || 0}
										index={i}>
										{!classResponse ? (
											<div className={`option ${selected ? 'selected' : ''}`}>
												{wrong ? <FaTimes /> : selected ? <FaCheck /> : <></>}
											</div>
										) : (
											<></>
										)}
									</ChoiceBox>
								)
							})}
						</ChoiceWrapper>
					</>
				}
			/>
		) : review === 'OVERVIEW' ? (
			<ReviewMultipleSelect
				index={props.questionIndex}
				studentAnswer={studentResponse?.answer}
				question={question}
			/>
		) : (
			<Fragment />
		)

	return <Fragment />
}
export default StudentMultipleChoice
